/**
 * Generic Upload Service
 * Provides a unified interface for file uploads with support for multiple backends
 */

import { UPLOAD_CONFIG } from '@/config';

class UploadService {
  constructor() {
    this.backend = UPLOAD_CONFIG.UPLOAD_BACKEND || 'local';
    this.baseApiUrl = UPLOAD_CONFIG.API_BASE_URL || 'https://api.kewaar.com/api/admin';
  }

  /**
   * Get authentication headers
   */
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    };
  }

  /**
   * Validate file type against allowed types
   */
  validateFileType(file, allowedTypes = null) {
    const allAllowedTypes = allowedTypes || [
      ...UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES,
      ...UPLOAD_CONFIG.ALLOWED_AUDIO_TYPES,
      ...UPLOAD_CONFIG.ALLOWED_VIDEO_TYPES,
      ...UPLOAD_CONFIG.ALLOWED_DOCUMENT_TYPES,
    ];

    if (!allAllowedTypes.includes(file.type)) {
      throw new Error(`File type ${file.type} is not allowed. Allowed types: ${allAllowedTypes.join(', ')}`);
    }
  }

  /**
   * Validate file size
   */
  validateFileSize(file, maxSize = null) {
    const maxFileSize = maxSize || UPLOAD_CONFIG.MAX_FILE_SIZE;
    if (file.size > maxFileSize) {
      const maxSizeMB = maxFileSize / 1024 / 1024;
      throw new Error(`File size exceeds maximum limit of ${maxSizeMB}MB`);
    }
  }

  /**
   * Generate a unique filename
   */
  generateFileName(originalName) {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    return `uploads/${timestamp}-${randomString}.${extension}`;
  }

  /**
   * Upload file using local/server backend
   */
  async uploadToServer(file, onProgress = null) {
    const formData = new FormData();
    formData.append('file', file);
    
    // Since we only have the /sleep1 endpoint, we need to send minimal required data
    // to create a temporary upload record that can be used later
    const timestamp = Date.now();
    const tempTitle = `Upload_${timestamp}`;
    
    // Add minimal required fields for the sleep1 endpoint
    formData.append('title', tempTitle);
    formData.append('title_ar', tempTitle);
    formData.append('type', 'bedtime'); // Default type
    formData.append('duration', '1');
    formData.append('upload_only', 'true'); // Flag to indicate this is just a file upload

    // Add required URL fields with placeholder values to prevent SQL errors
    const dummyUrl = `placeholder-upload-${timestamp}`;
    formData.append('audio_url', dummyUrl);
    formData.append('audio_url_ar', dummyUrl);
    formData.append('cover_photo', dummyUrl);
    formData.append('cover_photo_ar', dummyUrl);
    formData.append('main_photo', dummyUrl);
    formData.append('voice_over', dummyUrl);
    formData.append('video_over', dummyUrl);
    formData.append('video_over_ar', dummyUrl);
    
    // Add other potentially required fields
    formData.append('details', `Temporary upload record created at ${new Date().toISOString()}`);
    formData.append('details_ar', `سجل تحميل مؤقت تم إنشاؤه في ${new Date().toISOString()}`);
    formData.append('display_type', 'still');

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentage = Math.round((event.loaded / event.total) * 100);
            onProgress(percentage);
          }
        });
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            // Extract the file URL from the response
            // The response might contain the created record with file URLs
            let fileUrl = null;
            
            if (response.data) {
              // Look for file URL in various possible fields
              fileUrl = response.data.cover_photo || 
                       response.data.main_photo || 
                       response.data.audio_url || 
                       response.data.video_over ||
                       response.data.voice_over ||
                       response.url ||
                       response.fileUrl;
            } else {
              fileUrl = response.url || response.fileUrl;
            }
            
            resolve({
              fileUrl: fileUrl,
              fileName: response.data?.title || file.name,
              originalName: file.name,
              size: file.size,
              type: file.type,
              recordId: response.data?.id // In case we need to clean up later
            });
          } catch (error) {
            reject(new Error('Invalid response format'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject(new Error(errorResponse.message || `Upload failed: ${xhr.statusText}`));
          } catch (parseError) {
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        }
      };

      xhr.onerror = () => {
        reject(new Error('Upload failed due to network error'));
      };

      // Use the only available endpoint
      xhr.open('POST', `${this.baseApiUrl}/sleep1`);
      
      // Set auth headers (but don't set Content-Type for FormData - browser will set it automatically)
      const headers = this.getAuthHeaders();
      Object.keys(headers).forEach(key => {
        if (key !== 'Content-Type') { // Don't override Content-Type for FormData
          xhr.setRequestHeader(key, headers[key]);
        }
      });

      xhr.send(formData);
    });
  }

  /**
   * Upload file using mock/development backend
   */
  async uploadMock(file, onProgress = null) {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        if (onProgress) onProgress(progress);
        
        if (progress >= 100) {
          clearInterval(interval);
          resolve({
            fileUrl: `https://mock-cdn.example.com/uploads/${this.generateFileName(file.name)}`,
            fileName: this.generateFileName(file.name),
            originalName: file.name,
            size: file.size,
            type: file.type,
          });
        }
      }, 100);
    });
  }

  /**
   * Main upload method that routes to the appropriate backend
   */
  async uploadFile(file, options = {}) {
    const {
      onProgress = null,
      allowedTypes = null,
      maxSize = null,
      backend = null
    } = options;

    if (!file) {
      throw new Error('No file provided for upload');
    }

    // Validate file
    this.validateFileType(file, allowedTypes);
    this.validateFileSize(file, maxSize);

    // Determine which backend to use
    const uploadBackend = backend || this.backend;

    try {
      switch (uploadBackend) {
        case 'mock':
          return await this.uploadMock(file, onProgress);
        case 'server':
        case 'local':
        default:
          return await this.uploadToServer(file, onProgress);
      }
    } catch (error) {
      console.error(`Upload failed with backend ${uploadBackend}:`, error);
      throw error;
    }
  }

  /**
   * Upload with validation (main public method)
   */
  async uploadWithValidation(file, onProgress = null, options = {}) {
    return await this.uploadFile(file, { ...options, onProgress });
  }

  /**
   * Check if upload service is configured
   */
  isConfigured() {
    switch (this.backend) {
      case 'server':
      case 'local':
        return Boolean(this.baseApiUrl);
      case 'mock':
        return true; // Mock is always available
      default:
        return false;
    }
  }

  /**
   * Get upload backend type
   */
  getBackendType() {
    return this.backend;
  }

  /**
   * Test upload connectivity
   */
  async testConnection() {
    // Create a small test image file (1x1 pixel PNG)
    // This is a valid base64-encoded 1x1 transparent PNG
    const base64Data = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    const testBlob = new Blob([bytes], { type: 'image/png' });
    const testFile = new File([testBlob], 'test-upload.png', { type: 'image/png' });

    try {
      const result = await this.uploadWithValidation(testFile, (progress) => {
        console.log(`Test upload progress: ${progress}%`);
      });
      
      return {
        success: true,
        message: `Successfully connected to ${this.backend} upload backend`,
        details: result
      };
    } catch (error) {
      return {
        success: false,
        message: `Connection to ${this.backend} backend failed: ${error.message}`,
        error: error.message
      };
    }
  }
}

// Create and export a singleton instance
const uploadService = new UploadService();
export default uploadService; 